"use client";

import { useFeatureFlagEnabled, usePostHog } from "posthog-js/react";
import { useEffect, useRef } from "react";
import { buttonClass } from "@/components/ui/button";
import { monthlyPriceDisplay } from "@/lib/billing/pricing-plans";
import {
	PRO_MONTHLY_COACHING_SESSIONS,
	PRO_MONTHLY_COVER_LETTERS,
	PRO_MONTHLY_RESUME_GENERATIONS,
} from "@/lib/billing/pro-limits";

/** Sole switch for this card: ENABLED = renders, absent/DISABLED = today's page. */
export const JOBDETAIL_PRO_CARD_FLAG = "jobdetail-pro-card";

/**
 * Sole switch for the price line (card #7659 rung b, sibling of
 * `jobslist-pro-card-price`): ENABLED = the card quotes the monthly Pro price,
 * absent/DISABLED = today's copy. Every seeker Pro card shows 0 clicks across
 * 586 views, so the visitor has no price to weigh. Price text only, no
 * payment-method claim.
 */
export const JOBDETAIL_PRO_CARD_PRICE_FLAG = "jobdetail-pro-card-price";

/**
 * Pro entry below the Apply block on job detail, the highest-traffic seeker
 * surface (937 views/7d, 0 checkouts). Client island (the page is
 * edge-cached, so the flag must not be read server-side). Off/absent renders nothing.
 */
export function JobDetailProCard({ locale }: { locale: "id" | "en" }) {
	const enabled = useFeatureFlagEnabled(JOBDETAIL_PRO_CARD_FLAG) === true;
	const showPrice =
		useFeatureFlagEnabled(JOBDETAIL_PRO_CARD_PRICE_FLAG) === true;
	const posthog = usePostHog();
	const viewedRef = useRef(false);
	const isId = locale === "id";

	useEffect(() => {
		if (!enabled || viewedRef.current) return;
		viewedRef.current = true;
		posthog?.capture("jobdetail_pro_card_viewed", { locale, price: showPrice });
	}, [enabled, posthog, locale, showPrice]);

	if (!enabled) return null;
	const price = monthlyPriceDisplay(locale);

	return (
		<section
			aria-label={isId ? "Upgrade ke Pro" : "Upgrade to Pro"}
			data-testid="jobdetail-pro-card"
			className="mt-4 flex flex-col gap-3 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-center sm:justify-between"
		>
			<p className="text-sm text-foreground">
				{isId
					? `Melamar lowongan ini? Pro ${showPrice ? `${price}/bulan: ` : ""}memberi ${PRO_MONTHLY_COVER_LETTERS} surat lamaran AI, ${PRO_MONTHLY_RESUME_GENERATIONS} CV, dan ${PRO_MONTHLY_COACHING_SESSIONS} sesi coaching per bulan.`
					: `Applying to this job? Pro ${showPrice ? `${price}/month: ` : ""}includes ${PRO_MONTHLY_COVER_LETTERS} AI cover letters, ${PRO_MONTHLY_RESUME_GENERATIONS} resumes and ${PRO_MONTHLY_COACHING_SESSIONS} coaching sessions a month.`}
			</p>
			<a
				href={`/${locale}/pricing?intent&src=job_detail`}
				data-testid="saved-searches-pro-cta"
				onClick={() =>
					posthog?.capture("jobdetail_pro_card_clicked", {
						locale,
						price: showPrice,
					})
				}
				className={buttonClass({ size: "sm" })}
			>
				{isId ? "Lihat paket Pro" : "See Pro plans"}
			</a>
		</section>
	);
}
